
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import App from 'next/app';
import localFont from 'next/font/local';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';
import { useState } from 'react';
import { ThemeProvider } from 'styled-components';
import THEME from '../theme';
import { AppCustomContext } from '@/context/index';
import '../theme/ckeditor.css';
import '../theme/reset.css';

/**
 * App params props sent from server at every request for every page.
 */
interface AppParams {
	/**
	 * Current selected language.
	 */
	readonly currentLang?: string,
	/**
	 * Current path of the visited page.
	 */
	readonly currentPath?: string,
}

/**
 * Possible hostname of the website.
 */
type PageHostname = 'localhost' | 'preview.bruce.work' | 'wip.bruce.work' | 'www.bruce.work' | undefined;

/**
 * Fonts declaration.
 */
const axiformaFont = localFont({
	src: [
		{
			path: '../public/fonts/Axiforma-Regular.woff2',
			style: 'normal',
			weight: '400',
		},
		{
			path: '../public/fonts/Axiforma-Regular.woff',
			style: 'normal',
			weight: '400',
		},
		{
			path: '../public/fonts/Axiforma-Regular.ttf',
			style: 'normal',
			weight: '400',
		},
	],
	display: 'swap',
	preload: true,
	variable: '--font-axiforma',
});

const MyApp = ({ Component, pageProps, appParams, pageHostname }: AppProps & {
	appParams?: AppParams;
	pageHostname: PageHostname;
}) => {

	const customPageProps = { ...pageProps, ...appParams };
	const AppCustomContextValue = {
		currentLocale: appParams?.currentLang || 'fr',
		currentPath: appParams?.currentPath ? appParams?.currentPath.split('/').slice(2).join('/') : '',
	};

	const [reactQueryClient] = useState(() =>
		new QueryClient({
			defaultOptions: {
				queries: {
					staleTime: 120 * 1000,
				},
			},
		}),
	);

	const developmentHostnames = ['localhost', 'preview.bruce.work', 'wip.bruce.work'];
	const gtmAuth = pageHostname && developmentHostnames.includes(pageHostname) ? 'MtZdUdOVKu1YEb-B3kQX9A' : '';
	const gtmPreview = pageHostname && developmentHostnames.includes(pageHostname) ? 'env-51' : '';
	const isDevelopment = developmentHostnames.includes(pageHostname || '');

	return (
		<ThemeProvider theme={THEME}>
			<AppCustomContext.Provider value={AppCustomContextValue}>
				<div className={axiformaFont.className}>
					<QueryClientProvider client={reactQueryClient}>
						<Component {...customPageProps} />
					</QueryClientProvider>
				</div>
				<Script id='gtm-datalayer-init' strategy='beforeInteractive'>
					{`
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
                `}
				</Script>
				<Script
					id='google-tag-manager'
					strategy={isDevelopment ? 'afterInteractive' : 'worker'}
					src={`https://www.googletagmanager.com/gtm.js?id=GTM-WPX78SC${gtmAuth ? `&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x` : ''}`}
					data-partytown-forward='["dataLayer.push"]'
				/>
			</AppCustomContext.Provider>
		</ThemeProvider>
	);
};

MyApp.getInitialProps = async (appContext: AppContext) => {

	const appProps: AppInitialProps = await App.getInitialProps(appContext);
	const appParams: AppParams = {
		currentLang: appContext.ctx.query.language as string,
		currentPath: (appContext.ctx.req as any).originalUrl,
	};
	// @ts-expect-error `ctx.req` is typed as an IncomingMessage; `hostname` is added by Express.
	const pageHostname: PageHostname = appContext.ctx.req.hostname;

	return { ...appProps, appParams, pageHostname };
};

export default appWithTranslation(MyApp);
